import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Folder } from 'lucide-react';
import { Button } from '../../ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../ui/dialog';
import {
    listFolderOptions,
    setFoldersForPolicy,
} from '../../../services/api/folderService';
import { useToast } from '../../../hooks/use-toast';
import {
    getGroupsForPolicy,
    PolicyGroupListItem,
} from '../../../services/api/policyService';
import LoadingRing from '../../ui-components/LoadingRing';
import { FolderPickerDialog } from '../../group/FolderPickerDialog';
import { FolderPathLabel } from '../../group/FolderPathLabel';

interface PolicyFoldersDialogProps {
    policyId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    canManage: boolean;
    onCountChange?: (count: number) => void;
}

const PolicyFolderPicker = ({
    policyId,
    open,
    onOpenChange,
    onCountChange,
}: PolicyFoldersDialogProps) => {
    const load = useCallback(async () => {
        const [folders, policyFolders] = await Promise.all([
            listFolderOptions(),
            getGroupsForPolicy(policyId),
        ]);
        onCountChange?.(policyFolders.length);
        return { folders, selectedIds: policyFolders.map((f) => f._id) };
    }, [policyId, onCountChange]);

    const apply = useCallback(
        (folderIds: string[]) => setFoldersForPolicy(policyId, folderIds),
        [policyId]
    );

    return (
        <FolderPickerDialog
            open={open}
            onOpenChange={onOpenChange}
            load={load}
            apply={apply}
            onApplied={(folderIds) => onCountChange?.(folderIds.length)}
        />
    );
};

const PolicyFolderList = ({
    policyId,
    open,
    onOpenChange,
    onCountChange,
}: PolicyFoldersDialogProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [folders, setFolders] = useState<PolicyGroupListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getGroupsForPolicy(policyId);
                setFolders(result);
                onCountChange?.(result.length);
            } catch {
                setError(t('departmentPolicies.foldersDialog.failedToLoad'));
                toast({
                    title: t('departmentPolicies.foldersDialog.failedToLoad'),
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [open, policyId, onCountChange, t, toast]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {t('departmentPolicies.foldersDialog.title')}
                    </DialogTitle>
                    <DialogDescription>
                        {t(
                            'departmentPolicies.foldersDialog.descriptionViewer'
                        )}
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <LoadingRing size="small" />
                    </div>
                ) : error ? (
                    <p className="py-4 text-sm text-destructive">{error}</p>
                ) : folders.length === 0 ? (
                    <p className="py-4 text-sm font-medium text-subtle-foreground">
                        {t('departmentPolicies.foldersDialog.notInFolder')}
                    </p>
                ) : (
                    <div className="max-h-72 space-y-2 overflow-y-auto">
                        {folders.map((folder) => (
                            <button
                                key={folder._id}
                                type="button"
                                className="flex w-full items-center gap-3 rounded-lg border border-subtle bg-card px-4 py-3 text-left hover:bg-accent"
                                onClick={() => {
                                    onOpenChange(false);
                                    navigate(`/group/${folder._id}`);
                                }}
                            >
                                <Folder
                                    aria-hidden="true"
                                    className="h-4 w-4 shrink-0 text-subtle-foreground"
                                />
                                <FolderPathLabel
                                    title={folder.title}
                                    parentTitle={folder.parentTitle}
                                    className="text-sm font-medium"
                                />
                            </button>
                        ))}
                    </div>
                )}

                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>
                        {t('departmentPolicies.foldersDialog.done')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default function PolicyFoldersDialog(props: PolicyFoldersDialogProps) {
    return props.canManage ? (
        <PolicyFolderPicker {...props} />
    ) : (
        <PolicyFolderList {...props} />
    );
}
